import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { apiFetch } from "../lib/api";
import { supabase } from "../lib/supabase";
import { useTheme } from "../hooks/useTheme";
import { useEmpresaAtiva } from "../hooks/useEmpresaAtiva";
import { useVerComo } from "../hooks/useVerComo";
import { useWhatsappNaoLidas } from "../hooks/useWhatsapp"; import { BadgeDaConciliacao } from "./BadgeDaConciliacao"; // nº de lances do extrato esperando o dono (Conciliação em uma lista, 11/09)
import { camadaDaRota, rotaDeTelemetria } from "../lib/operador";
import { MENU_DO_OPERADOR } from "./menuDoOperador";
import { cn } from "../lib/utils"; import { folgaDaBarraDoApp, folgaDaGavetaDoCelular } from "../lib/recorteDoAparelho"; // 📱 folga do recorte do aparelho (barra e gaveta) — ver o arquivo
import { skinAtiva } from "../lib/skin";
import { usePeleDaRota } from "../hooks/usePeleDaRota";
import { NAV, itemVisivelNaSkin, rotuloItem, moduloDoItem, itemLiberadoPorGrant, itemVisivelPelosModulos, type GrantModulos } from "../lib/nav";
import { ordemDosGrupos } from "../lib/ordemDosGrupos";
import { comFavoritosNoTopo } from "../lib/menuFavoritos";
import { DudaFlutuante } from "./DudaFlutuante"; /* a bolha da Duda chega por import() — fora do caminho crítico */
import { VerComoControl } from "./VerComoControl";
import { VerComoBanner } from "./VerComoBanner";
import { LogoEmpresa } from "./LogoEmpresa";
import { LogoVortek } from "./LogoVortek";
import { AcoesDoCabecalho } from "../ajuda/AcoesDoCabecalho";
import { AvisoConformidade } from "./AvisoConformidade";
import { TrocarSenhaModal } from "./TrocarSenhaModal";
import { ErrorBoundary } from "./ErrorBoundary"; import { BotoesDoPe } from "./BotoesDoPe"; // ajuda, senha e tema no pé do menu
import {
  Menu,
  X,
  LayoutDashboard,
  LogOut,
  BookOpen,
  ChevronDown,
  Pin,
  PinOff,
  Star,
} from "lucide-react";

// canaisDe/SiteCfg mudaram pra lib/canaisDoRodape.ts (extração do R2, 26/08).
import { canaisDe } from "../lib/canaisDoRodape";
import { opcoesDoRadar } from "../lib/radarDaDuda"; import { mesEmSaoPaulo } from "../lib/format";

// Itens de menu (NAV) e helpers de pele (itemVisivelNaSkin/rotuloItem) moram em
// ../lib/nav — arquivo puro, sem React, pra a nav ser testável sem renderizar a
// sidebar. O que fica aqui embaixo (areaDoItem/moduloDoItem) é a política de RBAC
// e módulos, que só o Layout consome.

// RBAC (142): a que ÁREA cada item do menu pertence. A matriz perfis_acesso
// (por empresa) diz quais áreas cada perfil enxerga. Derivado do grupo, com
// exceções por rota (Indicadores e Portal moram na VISÃO GERAL mas são áreas próprias).
function areaDoItem(item: { to: string; grupo: string }): string {
  if (item.to === "/bi" || item.to === "/bi-site") return "indicadores";
  if (item.to === "/portal") return "portal";
  // Reorg dos grupos (PESSOAS/COMUNICAÇÃO/RECURSOS) é SÓ visual — a ÁREA (RBAC)
  // de cada item realocado continua a mesma de antes, via exceção por rota.
  const AREA_POR_ROTA: Record<string, string> = {
    "/folha": "financeiro",
    "/colaboradores": "operacao", "/acesso": "operacao", "/rh": "operacao",
    "/prestadores": "operacao", "/ativos": "operacao", "/estoque": "operacao",
    "/equipe": "painel", "/comunicacao": "painel", "/whatsapp": "painel", "/mensagens": "sistema",
  };
  if (AREA_POR_ROTA[item.to]) return AREA_POR_ROTA[item.to];
  const porGrupo: Record<string, string> = {
    "VISÃO GERAL": "painel",
    // 🔒 CARTEIRA nasceu em 24/08 tirando Unidades e Implantação de DIAGNÓSTICO —
    // e a área de RBAC delas CONTINUA "auditoria", como manda a regra escrita
    // acima: reorg de grupo é SÓ visual. Sem esta linha as duas cairiam no
    // fallback "painel" e sumiriam do menu de quem tem perfil restrito à área
    // auditoria — um grupo novo apagando tela de gente, em silêncio.
    CARTEIRA: "auditoria",
    "DIAGNÓSTICO & MONITORAMENTO": "auditoria",
    "REGULARIZAÇÃO": "auditoria",
    "AUDITORIA E RECUPERAÇÃO": "auditoria",
    "AUDITORIA DE BOLETOS": "auditoria",
    COMERCIAL: "comercial",
    OPERAÇÃO: "operacao",
    FINANCEIRO: "financeiro",
    SOCIETÁRIO: "societario",
    BASE: "base",
    SISTEMA: "sistema",
  };
  return porGrupo[item.grupo] ?? "painel";
}

// (moduloDoItem mudou pra ../lib/nav — fonte única compartilhada com a tela de
// Acessos. areaDoItem fica aqui: só o menu consome.)

// Chave do grupo virtual de favoritos (não é um grupo do NAV).
const FAV_KEY = "⭐ Favoritos";

/**
 * "OUTRA ABA FOI PRA OUTRA EMPRESA."
 *
 * Duas abas do painel em empresas diferentes era uma armadilha SILENCIOSA: a
 * empresa ativa morava no localStorage, que é global ao navegador, então a
 * última aba a trocar mandava em todas. A aba que ficou pra trás continuava
 * mostrando M5 na tela e passava a FALAR Vitrine com o servidor — foi assim que
 * o ORC-2026-0033 tomou "orcamento_nao_encontrado" no Criar v2 (27/07).
 *
 * O conserto de verdade é o header por aba (lib/api.ts): esta aba continua na
 * empresa dela, e a escrita sai certa. Esta faixa existe pro dono não ficar sem
 * entender por que a outra aba mostra outra coisa — a trava é invisível, a
 * confusão não seria.
 *
 * ⚠️ Na camada PLATAFORMA (VORTEK) a faixa cala. Ela promete "o que você salvar
 * aqui vai pra <empresa>", e no painel da plataforma isso é FALSO — as telas de
 * lá são cross-tenant por desenho. Em 27/07 essa promessa apareceu junto do
 * cabeçalho VORTEK e da lista "todas as empresas", três vozes discordando na
 * mesma tela. Quem diz a camada é `camadaDaRota` — fonte única.
 */
function AvisoTrocaEmOutraAba() {
  const { trocaEmOutraAba, ativa } = useEmpresaAtiva();
  const { pathname } = useLocation();
  if (camadaDaRota(pathname) === "plataforma") return null;
  if (!trocaEmOutraAba || !ativa) return null;
  return (
    <div
      role="status"
      className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
    >
      Outra aba passou a usar <strong>{trocaEmOutraAba}</strong>. Esta aba continua
      em <strong>{ativa.nome}</strong> — o que você salvar aqui vai pra{" "}
      <strong>{ativa.nome}</strong>.
    </div>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { user, signOut, getAccessToken } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { ativa: empresaAtiva } = useEmpresaAtiva();
  const { alvo: verComo } = useVerComo();
  const loc = useLocation();
  const navigate = useNavigate();
  usePeleDaRota(); // 👑 R2: plataforma é SEMPRE v2 + marca da casa (lib/skin)
  const role = user?.role ?? "admin";
  // Papel/usuário EFETIVOS: na lente "ver como", o menu é do ALVO; sem lente, do próprio.
  const efRole = verComo?.role ?? role;
  const efUserId = verComo?.id ?? user?.id;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [trocarSenha, setTrocarSenha] = useState(false);

  // Telemetria de adoção: 1 pageview por navegação; nome da tela via `rotaDeTelemetria`
  // (lib/operador.ts). Throttle 30s por rota. Erro aqui nunca trava a navegação.
  const ultimoPv = useRef<{ rota: string; t: number } | null>(null);
  useEffect(() => {
    if (!user || !empresaAtiva?.id) return;
    const rota = rotaDeTelemetria(loc.pathname);
    const agora = Date.now();
    const ult = ultimoPv.current;
    if (ult && ult.rota === rota && agora - ult.t < 30000) return;
    ultimoPv.current = { rota, t: agora };
    apiFetch("/telemetria/pagina", getAccessToken(), {
      method: "POST",
      body: JSON.stringify({ rota }),
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, empresaAtiva?.id]);

  // Estado de grupos colapsados — persiste em localStorage.
  // Default ENXUTO (chave -v2): o menu nasce TODO FECHADO, abrindo só o grupo
  // da tela atual. O usuário abre o que quiser e a escolha fica salva. A chave
  // foi versionada (-v2) pra que todo mundo pegue o novo default uma vez.
  const [colapsados, setColapsados] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem("mrg-sidebar-colapsados-v2");
      if (raw) return new Set<string>(JSON.parse(raw));
      // Primeira vez com o menu enxuto: descobre o grupo da rota atual…
      const p = loc.pathname;
      let best: any = null;
      for (const item of NAV) {
        const to = item.to as string;
        const hit = to === "/" ? p === "/" : p === to || p.startsWith(to + "/");
        if (hit && (!best || to.length > (best.to as string).length)) best = item;
      }
      const ativo = best ? ((best as any).inativo ? "EM BREVE" : best.grupo) : null;
      // …e fecha todos os outros.
      const todos = new Set<string>(NAV.map((i) => ((i as any).inativo ? "EM BREVE" : i.grupo)));
      if (ativo) todos.delete(ativo);
      return todos;
    } catch {
      return new Set<string>();
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("mrg-sidebar-colapsados-v2", JSON.stringify(Array.from(colapsados)));
    } catch {}
  }, [colapsados]);
  function toggleGrupo(g: string) {
    setColapsados((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  }

  // ⭐ Favoritos: itens fixados pelo usuário ganham um grupo no topo (atalho
  // rápido). Casa com o menu enxuto — os grupos nascem fechados, mas o que você
  // mais usa fica sempre à mão. Persiste em localStorage (por navegador).
  const [favoritos, setFavoritos] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem("mrg-sidebar-favoritos");
      return raw ? new Set<string>(JSON.parse(raw)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("mrg-sidebar-favoritos", JSON.stringify(Array.from(favoritos)));
    } catch {}
  }, [favoritos]);
  function toggleFav(to: string) {
    setFavoritos((prev) => {
      const next = new Set(prev);
      if (next.has(to)) next.delete(to);
      else next.add(to);
      return next;
    });
  }

  // Sugestões de favorito dispensadas — valem só pelo MÊS corrente. Vira o mês,
  // a lista de mais usados é reapresentada do zero (inclusive o que foi dispensado):
  // a sugestão "roda 1x por mês" em vez de sumir pra sempre.
  const mesCorrente = mesEmSaoPaulo(); // YYYY-MM
  const [dispensadas, setDispensadas] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem("mrg-sidebar-sugestoes-dispensadas");
      if (!raw) return new Set<string>();
      const obj = JSON.parse(raw) as { mes?: string; rotas?: string[] };
      if (obj.mes !== mesCorrente) return new Set<string>(); // mês novo → zera
      return new Set<string>(obj.rotas ?? []);
    } catch {
      return new Set<string>();
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        "mrg-sidebar-sugestoes-dispensadas",
        JSON.stringify({ mes: mesCorrente, rotas: Array.from(dispensadas) }),
      );
    } catch {}
  }, [dispensadas, mesCorrente]);
  function dispensarSugestao(to: string) {
    setDispensadas((prev) => new Set(prev).add(to));
  }

  // Sidebar recolhível: o alfinete FIXA (pinned). Quando não está fixa, fica
  // recolhida (só ícones) e expande ao passar o mouse (overlay, sem empurrar o
  // conteúdo). Default fixa, pra não mudar o comportamento de quem já usa.
  const [pinned, setPinned] = useState<boolean>(() => {
    try {
      return localStorage.getItem("mrg-sidebar-pinned") !== "false";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("mrg-sidebar-pinned", String(pinned));
    } catch {}
  }, [pinned]);
  const [hoverSidebar, setHoverSidebar] = useState(false);
  const expandido = pinned || hoverSidebar;

  // Identidade visual: cor de fundo da sidebar (preto M5 / navy Regula),
  // cor de acento (dourado / laranja) e nome.
  // Fallback pro padrão M5 antes do switcher carregar.
  // No Painel do Operador (camada VORTEK, acima dos clientes) a marca é VORTEK,
  // não a do cliente ativo. Fundo levemente distinto pra reforçar "outro nível".
  //
  // 🚨 QUEM DECIDE A CASCA É A ROTA — nunca o crachá de quem está olhando.
  //
  // O achado #35 (05/07) tinha colado um `&& is_owner` aqui pra um `cliente` que
  // digitasse /operador na mão não ver a marca VORTEK. Isso virou a MESMA doença
  // das três vozes do #1433, só que invertida: um admin NÃO-owner em /operador/*
  // recebia a casca da EMPRESA (logo do cliente, menu do cliente, faixa "o que
  // você salvar aqui vai pra <empresa>") por cima de conteúdo da PLATAFORMA — e
  // discordando do próprio EmpresaSwitcher ao lado, que sempre foi só pela rota e
  // já escrevia "VORTEK". Duas vozes brigando no mesmo cabeçalho.
  //
  // O caso do #35 continua fechado, e pelo lugar certo: `PrivateRoute soOperador`
  // manda todo papel `cliente` pro /portal antes de renderizar qualquer casca.
  // Quem não é dono da plataforma e insiste na URL vê a casca VORTEK (a verdade
  // sobre ONDE está) com "Acesso restrito" no conteúdo (a verdade sobre o que
  // pode) — as duas concordando. Fonte única: camadaDaRota (lib/operador.ts).
  const onOperador = camadaDaRota(loc.pathname) === "plataforma";
  // Barra ESCURA em todos os clientes e temas (operador num escuro distinto).
  const sidebarBgColor = onOperador ? "hsl(var(--barra-fundo-plataforma))" : "hsl(var(--barra-fundo))";
  const accentColor = "hsl(var(--accent-barra))"; // 🚦 acento da MARCA. NÃO troque por --accent: 2,58:1 na barra. Ver globals.css
  // Cross-tenant (P0): sem empresa ativa NÃO fingir ser M5 — era isso que fazia o Painel
  // pintar dados de outro tenant como "M5" na janela antes do header X-Empresa-Id chegar.
  // Slug "" → temComunicacao/temWhats/itens-por-empresa ficam falsos (não disparam sem header).
  const empresaNome = empresaAtiva?.nome ?? "…";
  const empresaSlug = empresaAtiva?.slug ?? "";
  const canais = canaisDe(empresaAtiva?.site_config);

  // Selo de e-mails não lidos no item "Agenda & E-mail" (só onde o módulo aparece).
  const temComunicacao = role === "admin" && (empresaSlug === "m5group" || empresaSlug === "atomo");
  const unread = useQuery({
    queryKey: ["zoho-mail-unread", empresaAtiva?.id],
    queryFn: () => apiFetch<{ total: number; por_conta?: { id: string; email: string; unread: number }[] }>("/zoho/mail/unread", getAccessToken()),
    enabled: temComunicacao && !!empresaAtiva?.id,
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000, // checa a cada 2 min
    refetchOnWindowFocus: true,
  });
  const naoLidos = unread.data?.total ?? 0;

  // Selo de WhatsApp não lido no item "WhatsApp".
  const qc = useQueryClient();

  // 🔴 A TELA PERGUNTA, NÃO ADIVINHA MAIS.
  //
  // Aqui havia `papel === admin|encarregado && slug === m5group|atomo`. O
  // servidor, porém, decide por uma LISTA NOMINAL de usuários
  // (`site_config.whatsapp_acesso`). As duas réguas discordavam, e discordância
  // entre tela e servidor não dá erro — dá experiência quebrada em silêncio.
  //
  // Medido em produção em 11/08: na M5 a TELA liberava 5 admins e o SERVIDOR
  // liberava 2. As 3 pessoas do meio viam o item de menu, o painel batia em
  // `/nao-lidas` de 20 em 20 segundos, levava 403, e o selo ficava vazio pra
  // sempre — 195 negativas entre 13/07 e 11/08. O comentário do guarda no
  // servidor já prometia que "o item some sozinho"; nunca sumiu, porque
  // ninguém perguntava.
  //
  // 🟥 14/08 — CONSERTAR AQUI NÃO BASTAVA, e o número provou: o `SinoNotificacoes`
  // chamava a MESMA queryKey sem perguntar nada, e no React Query duas consultas
  // de mesma key são UMA só, que dispara se QUALQUER observador estiver ligado.
  // Efeito líquido do conserto de um lado: zero (3,2 → 3,3 negativas/dia).
  // A régua agora é única e mora em `hooks/useWhatsapp` — ver o porquê lá.
  const { total: waNaoLidos, podeVer: temWhats } = useWhatsappNaoLidas();

  // Redesign v2: a pele ativa decide se os itens `soV2` (inbox "Precisa de você")
  // aparecem. Só aparência/adição — a v1 não muda.
  const skinV2 = skinAtiva() === "v2";
  // Badge do "Precisa de você": a contagem da MESMA fila do radar (fonte única com
  // a bolha da Duda). Só quando a v2 mostra o item e é operador com empresa ativa.
  const podeRadar = role === "admin" || role === "encarregado";
  const radarBadge = useQuery({
    // É a igualdade da chave que faz as cinco telas dividirem UMA requisição por
    // minuto — por isso ela mora numa fonte só (`lib/radarDaDuda`).
    ...opcoesDoRadar(empresaAtiva?.id, getAccessToken()),
    enabled: skinV2 && podeRadar && !!empresaAtiva?.id,
  });
  const precisaCount = radarBadge.data?.total ?? 0;

  // Gating por PILAR contratado: no portal do cliente (ou na lente "ver como"
  // cliente), só aparecem os pilares que ele contratou (propostas_ativas das
  // unidades dele). Operador vê todos. Unidade sem proposta configurada = tudo.
  const ehClienteRegula = (empresaSlug === "regula360" || empresaSlug === "vitrine-reg") && efRole === "cliente";
  const propostasQ = useQuery({
    queryKey: ["nav-pilares", empresaAtiva?.id, verComo?.id ?? "self"],
    queryFn: () => apiFetch<{ data: Array<{ propostas_ativas?: string[] | null }> }>("/centros", getAccessToken()),
    enabled: ehClienteRegula && !!empresaAtiva?.id,
    staleTime: 60 * 1000,
  });
  // Base da sugestão de favorito: as rotas que o próprio usuário mais usa.
  const maisUsadasQ = useQuery({
    queryKey: ["sidebar-mais-usadas", empresaAtiva?.id],
    queryFn: () => apiFetch<{ data: { rota: string; n: number }[] }>("/telemetria/mais-usadas", getAccessToken()).then((r) => r.data),
    enabled: !onOperador && !!empresaAtiva?.id,
    staleTime: 5 * 60 * 1000,
  });

  const pilaresContratados: Set<string> | null = (() => {
    if (!ehClienteRegula) return null;
    const cs = propostasQ.data?.data ?? [];
    if (cs.length === 0) return null;
    if (cs.some((c) => !c.propostas_ativas || c.propostas_ativas.length === 0)) return null;
    const s = new Set<string>();
    cs.forEach((c) => (c.propostas_ativas ?? []).forEach((p) => s.add(p)));
    return s;
  })();

  // Tempo real GLOBAL (sempre montado): toda msg nova atualiza o selo (sino +
  // sidebar usam a mesma key wa-nao-lidas) e a lista de conversas, sem esperar
  // o polling. setAuth garante o JWT do usuário no canal (RLS por empresa).
  useEffect(() => {
    if (!temWhats) return;
    const token = getAccessToken();
    if (token) supabase.realtime.setAuth(token);
    const ch = supabase
      .channel("wa-badge-global")
      .on("postgres_changes", { event: "*", schema: "public", table: "whatsapp_mensagens" }, () => {
        qc.invalidateQueries({ queryKey: ["wa-nao-lidas"] });
        qc.invalidateQueries({ queryKey: ["wa-chats"] });
        qc.invalidateQueries({ queryKey: ["wa-msgs"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "whatsapp_chats" }, () => {
        qc.invalidateQueries({ queryKey: ["wa-nao-lidas"] });
        qc.invalidateQueries({ queryKey: ["wa-chats"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temWhats]);

  // Título da aba: no Operador = marca VORTEK; dentro de um cliente = marca dele.
  useEffect(() => {
    document.title = onOperador ? "VORTEK · AI Operating System" : `${empresaNome} · Painel`;
  }, [onOperador, empresaNome]);

  // RBAC (142): áreas que o PERFIL do usuário nesta empresa enxerga. perfil 'admin'
  // (ou sem matriz) = sem restrição extra. O perfil só RESTRINGE além do papel global.
  const perfil = empresaAtiva?.perfil ?? "admin";
  const matriz = empresaAtiva?.perfis_acesso ?? null;
  const efPerfil = verComo?.perfil ?? perfil;
  const areasPermitidas =
    efPerfil === "admin" || !matriz ? null : matriz[efPerfil] ?? [];

  // RBAC modelo direto (nível 2): grant usuário×módulo/item DESTA pessoa nesta
  // empresa (empresa_users.modulos). null = herda tudo que a empresa contratou.
  // Na lente "ver como" de OUTRO usuário não temos o grant do alvo carregado —
  // esse preview segue governado pelo perfil/pilar (caminho antigo), então aqui
  // herda (null) pra não esconder demais. Fonte: ../lib/nav.itemLiberadoPorGrant.
  const meuGrant: GrantModulos = verComo ? null : (empresaAtiva?.meu_grant ?? null);

  // GUARDA DE ROTA do perfil TST: esconder do menu não basta — URL digitada
  // (deep-link) renderizava qualquer tela (achado do live-test, 01/07). Fora da
  // whitelist → volta pra Colaboradores. (Defesa em conjunto com o servidor,
  // que já corta salário/PIX/banco e barra financeiro/painel pela área.)
  // "/pessoas" é ALIAS de "/colaboradores" (App.tsx serve o MESMO componente nas duas
  // rotas, desde que o rótulo v2 virou "Pessoas"). Sem ele na lista, o TST que digitasse
  // /pessoas era expulso da própria tela. Não libera nada novo — é a mesma página.
  const TST_ROTAS = ["/colaboradores", "/pessoas", "/acesso"];
  const tstForaDaArea =
    efRole !== "cliente" && efPerfil === "tst" && !TST_ROTAS.some((r) => loc.pathname === r || loc.pathname.startsWith(r + "/"));
  useEffect(() => {
    if (tstForaDaArea) navigate("/colaboradores", { replace: true });
  }, [tstForaDaArea, navigate]);

  const visiveis = NAV.filter((item) => {
    // Item exclusivo do DONO da empresa ativa (self-service): mostra só pro dono,
    // independente de role/RBAC. Esconde de todo o resto (inclusive outros admins).
    if ((item as any).donoOnly) return !!empresaAtiva?.dono_user_id && empresaAtiva.dono_user_id === efUserId;
    // Pele v2: item aditivo (soV2, ex. inbox "Precisa de você") só aparece com a v2
    // ligada; item que a v2 absorveu (soV1) some sob a v2 — hoje NENHUM item usa
    // soV1 (o Controle de Acesso usava, e não tinha sido absorvido: ../lib/nav).
    // Fora isso a v1 continua byte-idêntica. Ver itemVisivelNaSkin em ../lib/nav.
    if (!itemVisivelNaSkin(item as any, skinV2)) return false;
    if (!(item.roles as readonly string[]).includes(efRole)) return false;
    // 05/09: a allowlist por SLUG (`item.empresas`) morreu — quem esconde tela agora é só o
    // módulo (empresas.modulos), com a matriz completa por empresa gravada pela migration
    // `menu_por_modulo_matriz_por_empresa`. Empresa COM a coluna só vê módulo marcado `true`
    // (deny-by-default, o que quem vende módulo espera); sem a coluna, vê tudo (legado).
    // Fonte única: api/lib/catalogoModulos.itemVisivelPelosModulos (o teste de paridade a usa).
    // (Achado #46, auditoria 05/07): removido o ramo `ownerOnly` — nenhum item do
    // NAV declara essa flag, então a condição nunca disparava (código morto). O
    // gating do admin master hoje é in-page (guard is_owner na própria página).
    const modulo = moduloDoItem(item);
    if (!itemVisivelPelosModulos(modulo, empresaAtiva?.modulos)) return false;
    // Nível 2 (RBAC modelo direto): grant usuário×módulo/item nesta empresa.
    // Cliente é gated por papel + pilar (não pela allow-list interna). Núcleo
    // (moduloDoItem null) sempre passa; grant null herda tudo que a empresa contratou.
    if (efRole !== "cliente" && !itemLiberadoPorGrant(item, meuGrant)) return false;
    // Pilar contratado (só no portal do cliente / lente "ver como" cliente).
    const pilar = (item as any).pilar as string | undefined;
    if (pilaresContratados && pilar && !pilaresContratados.has(pilar)) return false;
    // Perfil "tst" (Técnico de Segurança do Trabalho): visão restrita a
    // Colaboradores + Controle de Acesso (pedido Átomo/Ruddy 01/07). Some todo o
    // resto do menu. O backend barra financeiro/folha/sócios pela matriz de áreas
    // (perfil tst = só "operacao") + requireArea.
    // Usa a MESMA constante da guarda de rota acima: eram duas listas iguais escritas
    // à mão, e uma podia crescer sem a outra — menu e URL discordando sobre o mesmo
    // perfil. As duas telas valem nas duas peles (o Controle de Acesso deixou de ser soV1).
    if (efRole !== "cliente" && efPerfil === "tst" && !TST_ROTAS.includes(item.to)) return false;
    // RBAC (perfis_acesso) é do TIME INTERNO. O cliente é gated por papel + pilar
    // contratado, não pela matriz interna — senão o pilar contratado some pra ele.
    if (efRole !== "cliente" && areasPermitidas && !areasPermitidas.includes(areaDoItem(item))) return false;
    return true;
  });
  // Ordem fixa dos grupos no menu (M5 Group — definida no painel pelo Igor).
  // Itens com grupo fora desta lista vão pro final, na ordem que aparecem.
  // No Regula a operação vem primeiro (Diagnóstico→Auditoria&Recuperação→Boletos),
  // Comercial depois. No M5 os grupos do Regula são vazios (não renderizam), então
  // Comercial fica logo após VISÃO GERAL como antes — sem mudança visível pro M5.
  // A ordem (uma por casa) mora em lib/ordemDosGrupos — dado puro, testável sem
  // renderizar a sidebar. A da Regula conta o fluxo do serviço; a do M5 é a histórica.
  const ORDEM_GRUPOS = ordemDosGrupos(empresaSlug, empresaAtiva?.marca); // a MARCA (dado) decide; slug é fallback
  // Módulos marcados `inativo` saem do grupo original e vão pro bucket
  // "EM BREVE" (no fim, recolhido por padrão). A rota continua acessível.
  // O rótulo é resolvido pela pele AQUI (rotuloItem): sob v2, "Colaboradores"→"Pessoas"
  // e "Folha de Pagamento"→"Folha"; na v1, o rótulo original.
  type NavRender = { to: string; label: string; icon: typeof LayoutDashboard; exact?: boolean };
  const gruposMap: Record<string, NavRender[]> = {};
  for (const item of visiveis) {
    const g = (item as any).inativo ? "EM BREVE" : item.grupo;
    (gruposMap[g] ??= []).push({ to: item.to, label: rotuloItem(item as any, skinV2, empresaSlug, efRole), icon: item.icon });
  }
  let grupos: Array<[string, NavRender[]]> = [];
  for (const g of ORDEM_GRUPOS) {
    if (g === "EM BREVE") continue; // menu enxuto: "Em breve" não aparece na sidebar
    if (gruposMap[g]) grupos.push([g, gruposMap[g]]);
  }
  // Quaisquer grupos não listados vão pro fim
  for (const [g, items] of Object.entries(gruposMap)) {
    if (g === "EM BREVE") continue;
    if (!ORDEM_GRUPOS.includes(g)) grupos.push([g, items]);
  }
  // No Painel do Operador (camada VORTEK) o menu lateral é o da PLATAFORMA — ver menuDoOperador.ts.
  if (onOperador) grupos = [["PLATAFORMA", MENU_DO_OPERADOR]];
  // Grupo "⭐ Favoritos" no topo (fora do Operador): reúne os itens fixados, na
  // mesma ordem em que aparecem no menu.
  //
  // 🟥 22/09 — O ATALHO SUMIA COM A TELA. O item favoritado era REMOVIDO do grupo
  // de origem ("vive só no topo, sem duplicar"), e com a barra rolada o dono
  // procurou Orçamentos em COMERCIAL, não achou, e o menu parecia ter perdido a
  // tela ("cadê a porcaria do módulo orçamentos"). Fixar é ganhar um atalho, não
  // mudar a tela de lugar: agora o item aparece NOS DOIS (topo e grupo), com a
  // estrelinha acesa nos dois. As chaves de render são por grupo, então repetir
  // não colide.
  const itensVisiveisFlat = grupos.flatMap(([, itens]) => itens);
  if (!onOperador) grupos = comFavoritosNoTopo(grupos, favoritos, FAV_KEY);
  // Sugestão de favorito (nudge contínuo): a rota que o usuário MAIS usa e que
  // ainda NÃO fixou nem dispensou. Uma de cada vez, no topo, com fixar/dispensar.
  // Some ao fixar ou dispensar; a próxima candidata assume. Lista vem ordenada ↓.
  const sugestaoFav = (() => {
    if (onOperador) return null;
    for (const { rota, n } of maisUsadasQ.data ?? []) {
      if (n < 4) break; // só nudge pra uso recorrente (4+ aberturas)
      if (rota === "/" || rota === loc.pathname) continue;
      if (favoritos.has(rota) || dispensadas.has(rota)) continue;
      const it = itensVisiveisFlat.find((x) => x.to === rota);
      if (it) return it;
    }
    return null;
  })();
  const SugIcon = sugestaoFav?.icon ?? LayoutDashboard;

  // Sidebar: preto profundo nos dois modos — identidade M5, SEM tema. O valor
  // mora em --barra-fundo (globals.css), junto do --accent-barra que vai em cima.
  const renderSidebar = (mostrarLabels: boolean) => (
    <>
      <div className={cn("py-5 border-b border-white/10 flex items-center", mostrarLabels ? "px-6 justify-between" : "px-0 justify-center")}>
        <div className={cn("flex items-center", mostrarLabels && "gap-3")}>
          {onOperador ? (
            <>
              <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "hsl(var(--accent-barra))" }}><LogoVortek size={24} color="#161410" /></div>
              {mostrarLabels && (
                <div>
                  <div className="text-lg font-semibold tracking-[0.18em] text-white leading-none">VORTEK</div>
                  <div className="text-[9px] tracking-[0.15em] uppercase font-medium mt-1 whitespace-nowrap" style={{ color: "hsl(var(--accent-barra))" }}>AI Operating System</div>
                </div>
              )}
            </>
          ) : (
            <>
              <LogoEmpresa size={40} onDark />
              {mostrarLabels && (
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase font-light" style={{ color: accentColor }}>
                    {empresaNome}
                  </div>
                  <div className="text-lg font-semibold tracking-tight text-white -mt-0.5">Painel</div>
                </div>
              )}
            </>
          )}
        </div>
        {mostrarLabels && (
          <div className="flex items-center gap-1">
            {/* Alfinete — fixa a barra aberta (só faz sentido no desktop) */}
            <button
              onClick={() => setPinned((p) => !p)}
              className="hidden md:inline-flex p-1.5 rounded-md hover:bg-white/10 transition-colors"
              style={{ color: pinned ? accentColor : "rgba(255,255,255,0.55)" }}
              title={pinned ? "Soltar a barra (recolhe ao tirar o mouse)" : "Fixar a barra aberta"}
              aria-label={pinned ? "Soltar barra lateral" : "Fixar barra lateral"}
            >
              {pinned ? <Pin className="h-4 w-4" /> : <PinOff className="h-4 w-4" />}
            </button>
            <button
              onClick={() => setMobileOpen(false)}
              className="md:hidden text-white/60 hover:text-white"
              aria-label="Fechar menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* "Ver como" — lente global (logo abaixo da logo). Só no contexto de
          empresa (não no menu do Operador) e só pra admin (o próprio controle
          se esconde pros demais papéis). */}
      {!onOperador && <VerComoControl mostrarLabels={mostrarLabels} accentColor={accentColor} />}

      {/* Redes & site da empresa — ícones no rodapé (do site_config; cada cliente
          preenche o seu em "Redes & Site" no BI do Site). Só no modo expandido. */}
      {mostrarLabels && !onOperador && canais.length > 0 && (
        <div className="flex items-center gap-1.5 px-6 py-3 border-b border-white/10">
          {canais.map((canal) => {
            const Icon = canal.icon;
            return (
              <a
                key={canal.href}
                href={canal.href}
                target="_blank"
                rel="noopener noreferrer"
                title={canal.label}
                aria-label={canal.label}
                className="p-1.5 rounded-md text-white/75 hover:bg-white/10 transition-colors"
                onMouseEnter={(e) => (e.currentTarget.style.color = accentColor)}
                onMouseLeave={(e) => (e.currentTarget.style.color = "")}
              >
                <Icon className="h-4 w-4" />
              </a>
            );
          })}
        </div>
      )}

      <nav
        className={cn(
          "flex-1 py-4 overflow-y-auto overflow-x-hidden",
          mostrarLabels ? "px-2" : "px-2",
          "[scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.18)_transparent]",
          "[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent",
          "[&::-webkit-scrollbar-thumb]:bg-white/15 [&::-webkit-scrollbar-thumb]:rounded-full",
          "hover:[&::-webkit-scrollbar-thumb]:bg-white/25"
        )}
      >
        {mostrarLabels && sugestaoFav && (
          <div className="mb-3 mx-1 rounded-lg border border-dashed px-2.5 py-2" style={{ borderColor: "rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.03)" }}>
            <div className="text-[10px] uppercase tracking-wider mb-1 px-1" style={{ color: "rgba(255,255,255,0.4)" }}>
              Sugestão · você usa muito
            </div>
            <div className="flex items-center gap-1">
              <Link
                to={sugestaoFav.to}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 flex-1 min-w-0 text-sm text-white/85 hover:text-white py-1"
              >
                <SugIcon className="h-4 w-4 shrink-0" style={{ color: accentColor }} />
                <span className="truncate">{sugestaoFav.label}</span>
              </Link>
              <button
                type="button"
                onClick={() => toggleFav(sugestaoFav!.to)}
                title="Fixar nos favoritos"
                className="shrink-0 rounded px-1.5 py-1 hover:bg-white/10 text-[11px] font-medium flex items-center gap-1"
                style={{ color: accentColor }}
              >
                <Star className="h-3.5 w-3.5" /> fixar
              </button>
              <button
                type="button"
                onClick={() => dispensarSugestao(sugestaoFav!.to)}
                aria-label="Dispensar sugestão por este mês"
                title="Agora não — volto a sugerir mês que vem"
                className="shrink-0 rounded p-1 hover:bg-white/10 text-white/40 hover:text-white/70"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
        {grupos.map(([grupo, itens]) => {
          const isClosed = colapsados.has(grupo);
          const grupoTemAtivo = itens.some(
            (it) => loc.pathname === it.to || (it.to !== "/" && loc.pathname.startsWith(it.to + "/"))
          );
          // Recolhido (só ícones): mostra todos os itens, sem cabeçalho de grupo.
          const aberto = mostrarLabels ? !isClosed || grupoTemAtivo : true;
          return (
          <div key={grupo} className="mb-3">
            {mostrarLabels && (
              <button
                type="button"
                onClick={() => toggleGrupo(grupo)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold tracking-[0.18em] uppercase hover:bg-white/5 rounded-md transition-colors"
                style={{ color: accentColor, opacity: 0.7 }}
                title={aberto ? "Recolher grupo" : "Expandir grupo"}
              >
                <span className="flex-1 text-left leading-tight">{grupo}</span>
                <ChevronDown
                  className="h-3 w-3 shrink-0 transition-transform"
                  style={{ transform: aberto ? "rotate(0deg)" : "rotate(-90deg)" }}
                />
              </button>
            )}
            {aberto && (
            <div className="space-y-0.5 mt-1">
              {itens.map((item) => {
                const Icon = item.icon;
                const active = (item as { exact?: boolean }).exact
                  ? loc.pathname === item.to
                  : loc.pathname === item.to ||
                    (item.to !== "/" && loc.pathname.startsWith(item.to + "/"));
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    title={mostrarLabels ? undefined : item.label}
                    className={cn(
                      "group relative flex items-center rounded-md text-sm font-medium transition-colors",
                      mostrarLabels ? "gap-3 pl-4 pr-3 py-2" : "justify-center px-0 py-2.5",
                      active
                        ? "bg-white/10 text-white"
                        : "text-white/65 hover:bg-white/5 hover:text-white"
                    )}
                  >
                    {active && (
                      <span
                        aria-hidden
                        className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full"
                        style={{ backgroundColor: accentColor }}
                      />
                    )}
                    <Icon
                      className="h-4 w-4 shrink-0"
                      style={active ? { color: accentColor } : { color: "rgba(255,255,255,0.5)" }}
                    />
                    {mostrarLabels && <span className="flex-1 truncate">{item.label}</span>}
                    {mostrarLabels && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(item.to); }}
                        title={favoritos.has(item.to) ? "Remover dos favoritos" : "Fixar nos favoritos"}
                        className={cn(
                          "shrink-0 rounded p-0.5 transition-opacity hover:bg-white/10",
                          favoritos.has(item.to) ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"
                        )}
                      >
                        <Star
                          className="h-3.5 w-3.5"
                          style={favoritos.has(item.to) ? { color: accentColor, fill: accentColor } : { color: "rgba(255,255,255,0.55)" }}
                        />
                      </button>
                    )}
                    {item.to === "/comunicacao" && naoLidos > 0 && (
                      <span
                        className={cn(
                          "rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none flex items-center justify-center",
                          mostrarLabels ? "px-1.5 py-0.5 min-w-[18px]" : "absolute top-1 right-1 h-2 w-2 p-0"
                        )}
                        title={`${naoLidos} não lido(s)`}
                      >
                        {mostrarLabels ? (naoLidos > 99 ? "99+" : naoLidos) : ""}
                      </span>
                    )}
                    {item.to === "/whatsapp" && waNaoLidos > 0 && (
                      <span
                        className={cn(
                          "rounded-full bg-emerald-500 text-white text-[10px] font-bold leading-none flex items-center justify-center",
                          mostrarLabels ? "px-1.5 py-0.5 min-w-[18px]" : "absolute top-1 right-1 h-2 w-2 p-0"
                        )}
                        title={`${waNaoLidos} não lido(s)`}
                      >
                        {mostrarLabels ? (waNaoLidos > 99 ? "99+" : waNaoLidos) : ""}
                      </span>
                    )}
                    {item.to === "/precisa-de-voce" && precisaCount > 0 && (
                      <span
                        className={cn(
                          "rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none flex items-center justify-center",
                          mostrarLabels ? "px-1.5 py-0.5 min-w-[18px]" : "absolute top-1 right-1 h-2 w-2 p-0"
                        )}
                        title={`${precisaCount} coisa(s) precisam de você`}
                      >
                        {mostrarLabels ? (precisaCount > 99 ? "99+" : precisaCount) : ""}
                      </span>
                    )} {item.to === "/conciliacao" && <BadgeDaConciliacao mostrarLabels={mostrarLabels} ativo={role === "admin" && !!empresaAtiva?.id} />}
                  </Link>
                );
              })}
            </div>
            )}
          </div>
          );
        })}
      </nav>

      <div className={cn("border-t border-white/10", mostrarLabels ? "p-4" : "p-2")}>
        {user?.is_owner && onOperador && (
          <Link
            to="/manual-de-bordo"
            onClick={() => setMobileOpen(false)}
            title="Manual de Bordo — acesso de emergência"
            className={cn(
              "flex items-center gap-2 rounded-md transition-colors",
              mostrarLabels ? "px-3 py-2 w-full mb-3" : "justify-center py-2 mb-2",
              loc.pathname === "/manual-de-bordo"
                ? "bg-white/10"
                : "hover:bg-white/10"
            )}
          >
            <BookOpen
              className="h-4 w-4 shrink-0"
              style={{ color: accentColor }}
            />
            {mostrarLabels && (
              <span className="text-sm font-medium" style={{ color: accentColor }}>
                Manual de Bordo
              </span>
            )}
          </Link>
        )}
        {mostrarLabels && (
          <>
            <div className="text-sm font-medium truncate text-white">{user?.nome ?? user?.email}</div>
            <div className="text-xs capitalize" style={{ color: accentColor, opacity: 0.8 }}>{role}</div>
          </>
        )}
        <div className={cn("flex items-center", mostrarLabels ? "justify-between mt-3" : "flex-col gap-2")}>
          <button
            onClick={signOut}
            title="Sair"
            className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0" /> {mostrarLabels && "Sair"}
          </button>
          <BotoesDoPe empilhado={!mostrarLabels} tema={theme} onTrocarSenha={() => setTrocarSenha(true)} onTrocarTema={toggleTheme} />
        </div>
      </div>
    </>
  );

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      <TrocarSenhaModal open={trocarSenha} onClose={() => setTrocarSenha(false)} />
      {/* Sidebar desktop — recolhível. Fixa (alfinete) = ocupa espaço; solta =
          recolhida (só ícones) que expande no hover SOBRE o conteúdo (overlay).
          Um spacer reserva o espaço no fluxo já que a aside é fixed. */}
      <aside
        onMouseEnter={() => !pinned && setHoverSidebar(true)}
        onMouseLeave={() => setHoverSidebar(false)}
        className={cn(
          "hidden md:flex flex-col print:hidden fixed left-0 top-0 bottom-0 z-40 transition-[width] duration-200 ease-out",
          expandido ? "w-64" : "w-16",
          !pinned && expandido && "shadow-2xl shadow-black/40"
        )}
        style={{ backgroundColor: sidebarBgColor }}
      >
        {renderSidebar(expandido)}
      </aside>
      <div
        className={cn("hidden md:block flex-shrink-0 transition-[width] duration-200", pinned ? "w-64" : "w-16")}
        aria-hidden
      />

      {/* Drawer mobile */}
      {mobileOpen && (
        <>
          <div
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 bg-black/40 z-40 md:hidden"
          />
          <aside
            className="fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] z-50 md:hidden flex flex-col shadow-xl"
            style={{ backgroundColor: sidebarBgColor, ...folgaDaGavetaDoCelular() }}
          >
            {renderSidebar(true)}
          </aside>
        </>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar — sticky, com hamburger mobile + botão de busca global */}
        <header className="flex items-center gap-3 px-4 py-3 border-b bg-card sticky top-0 z-30 print:hidden" style={folgaDaBarraDoApp()}>
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden text-foreground hover:text-primary"
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="text-sm font-semibold md:hidden">{onOperador ? "VORTEK" : `${empresaNome} · Painel`}</div>
          <div className="hidden md:flex flex-1" />
          <AcoesDoCabecalho />
        </header>

        {/* Banner da lente "Ver como" — vive UMA vez só, no <VerComoBanner/>
            logo abaixo (dentro do <main>). Havia aqui uma segunda faixa inline
            dizendo a MESMA coisa ("Você está vendo o painel como …"), então o
            operador levava dois avisos empilhados na mesma tela. */}

        <a href="#main-content" className="skip-link">Pular para o conteúdo</a>
        {/* 🫧 RESERVA DA BOLHA DA DUDA. Ela é `fixed` na viewport: sem este
            rodapé, o FIM de toda página termina embaixo dela — foi assim que o
            chip "Tudo certo" do card de Clima ficou coberto no Painel (Ruddy,
            29/07), e assim que ela já roubou clique no rodapé de outras telas.
            O valor mora em `--reserva-flutuante` (globals.css) pra ser o MESMO
            que as telas de altura fixa descontam. Na impressão, some. */}
        <main
          id="main-content"
          role="main"
          className="flex-1 overflow-auto pb-[var(--reserva-flutuante)] print:pb-0"
          tabIndex={-1}
        >
          {/* Faixa permanente do modo de visão (09/07): antes o único sinal
              vivia na sidebar e sumia quando recolhida. */}
          <VerComoBanner />
          <AvisoTrocaEmOutraAba />
          {/* Faixa "como suporte" REMOVIDA (v2.1): o retorno ao Operador já vive no
              seletor de tenant (EmpresaSwitcher → item "Operador"), então nada se
              perde e o painel do cliente não carrega mais um banner sticky. */}
          {/* 🛡️ O AVISO VAI ATÉ A PESSOA — em QUALQUER tela que ela possa ver.
              A tela de Conformidade já existia; você tinha que IR ATÉ ELA. E ninguém ia:
              no dia 14/07 o banco tinha, calado, um ASO VENCIDO HÁ 124 DIAS de alguém que
              está ATIVO e entrando em obra. O sistema não falhou — ele ficou CALADO, que
              é a mesma coisa.
              O acesso de cada um é respeitado por construção: a RLS decide o que a pessoa
              vê, e quem não pode ver não recebe aviso nenhum (o cliente nunca vê NR de
              funcionário). Pedido do Ruddy: "todos na Átomo têm que ser avisados,
              ATRAVÉS DO PAINEL". */}
          {/* Redesign v2: a conformidade vencida vira ITEM da fila da Duda (radar),
              não banner à parte — "uma fila, várias vitrines". Sob ?skin=v2 o banner
              some (o item aparece no peek do Painel e na lista do inbox); a v1 mantém
              o aviso fixo. Uma fonte só, sem duplicar. */}
          {!skinV2 && <AvisoConformidade />}
          <ErrorBoundary resetKey={loc.pathname}>{children}</ErrorBoundary>
        </main>
      </div>

      {/* Busca/navegação global = Duda (assistente de IA). A antiga paleta Ctrl+K
          foi removida: a Duda cobre busca + navegação + perguntas numa porta só. */}

      {/* Duda — assistente de IA flutuante, em toda tela. No Painel do Operador
          (dono, cross-tenant) ela troca o radar de tenant pelo radar da PLATAFORMA
          (teto de IA, crédito, cross-tenant, saúde) — ver ehPlataforma no widget.
          Assim não vaza dado de um tenant no cockpit do dono. */}
      <DudaFlutuante />
    </div>
  );
}
